import { usePageSetup } from '@/hooks/usePageSetup';
import { useTranslation, Trans } from 'react-i18next';
import { motion } from 'framer-motion';
import HeaderBack from "@/components/dashboard/Header/HeaderBack";

import MotionText from '@/components/common/Motion/MotionText';
import MotionDiv from '@/components/common/Motion/MotionDiv';
import ButtonLink from '@/components/common/Button/ButtonLink';

import { VAT_HOW_PDF } from '@/components/pages/Profile/ProfilePage';

import {
  PiChartBar,
  PiDownload,
  PiUpload,
  PiLink,
  PiYoutubeLogo,
  PiArrowRight,
  PiInfo
} from 'react-icons/pi';

export const VAT_LINKS = {
  boltInvoices: 'https://partners.bolt.eu/invoices',
  videoGuide: 'https://youtu.be/LmBFZMByiX4?t=391',
  vmiPayment: 'https://www.vmi.lt/evmi/kaip-moketi-imokas-i-vmi-saskaita',
  vmiAccounts: 'https://www.vmi.lt/evmi/saskaitos-ir-imoku-kodai',
  edsForm: 'https://deklaravimas.vmi.lt/lt/Formu_sarasas.aspx',
  vmiPaymentNews: 'https://deklaravimas.vmi.lt/lt/Korteles/Naujienos_kortele/Naujienos_kortele.aspx?newsitemid=0465e225-ef36-4019-b350-108204c9d303',
  sodraCalculator: 'https://sodra.lt/skaiciuokles/individualios-veiklos-skaiciuokle',
  sodraVSD: 'https://sodra.lt/imokos/vykdau-individualia-veikla',
  sodraPSD: 'https://sodra.lt/imokos/savarankiskai-moku-privalomojo-sveikatos-draudimo-psd-imokas',
  vmiGPM: 'https://www.vmi.lt/evmi/deklaruoti-pajamas',
  vmiGPMInfo: 'https://www.vmi.lt/evmi/pajamu-apmokestinimas-deklaravimas#kaip-apmokestinamos-individualios-veiklos-pajamos',
  sodraTarifai: 'https://sodra.lt/imoku-tarifai/imoku-tarifai-taikomi-savarankiskai-dirbantiems-asmenims'
};

interface TimelineStepProps {
  stepNumber: number;
  date: string;
  title: string;
  children: React.ReactNode;
  isLast?: boolean;
  delay?: number;
}

const TimelineStep = ({ stepNumber, date, title, children, isLast = false, delay = 0 }: TimelineStepProps) => (
  <MotionDiv delay={delay} className="flex gap-4">
    <div className="flex flex-col items-center">
      <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center font-semibold text-xs shrink-0">
        {stepNumber}
      </div>
      {!isLast && <div className="w-0.5 bg-zinc-200 flex-1 min-h-6 mt-1" />}
    </div>
    <div className={`flex-1 ${!isLast ? 'pb-10' : ''}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded">{date}</span>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <h3 className="font-semibold text-base text-zinc-900">{title}</h3>
      </div>
      <div className="space-y-4">
        {children}
      </div>
    </div>
  </MotionDiv>
);

interface StepItemProps {
  icon: React.ReactNode;
  children: React.ReactNode;
}

const StepItem = ({ icon, children }: StepItemProps) => (
  <div className="flex gap-3 items-start">
    <div className="text-zinc-500 mt-0.5">{icon}</div>
    <div className="text-sm text-zinc-700">{children}</div>
  </div>
);

interface StepLinkProps {
  href: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}

const StepLink = ({ href, icon, children, className }: StepLinkProps) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`flex gap-3 items-center text-sm transition-colors ${className || 'text-blue-600 hover:text-blue-800'}`}
  >
    <div>{icon}</div>
    <span className="underline">{children}</span>
    <PiArrowRight className="w-4 h-4" />
  </a>
);

export const DashboardVatMemoPage = () => {
  const { t } = useTranslation();
  usePageSetup(t('taxes.vat.pageTitle'), { scroll: true });

  return (
    <div className="h-full bg-white flex flex-col">
      <HeaderBack />
      <div className="flex flex-col px-5 pb-32">
        <MotionText
          as='h1'
          className='font-semibold text-2xl mb-4'>
          {t('taxes.vat.title')}
        </MotionText>
        <MotionText
          as='p'
          className='text-sm text-zinc-700 mb-8'
        >
          <Trans i18nKey="taxes.vat.description" components={{ strong: <strong /> }} />
        </MotionText>
        <MotionDiv delay={0.1} className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
          <p className="font-medium text-amber-900 mb-2">{t('taxes.vat.beforeStarting')}</p>
          <p className="text-sm text-amber-800 mb-3">
            {t('taxes.vat.registerPvmSvs')}
          </p>
          <StepLink href={VAT_HOW_PDF} icon={<PiLink className="w-4 h-4" />} className="text-amber-700 hover:text-amber-900">
            {t('taxes.vat.howToRegister')}
          </StepLink>
        </MotionDiv>

        <MotionText
          as='p'
          className='text-sm text-zinc-600 mb-6'
        >
          {t('taxes.vat.monthlySteps')}
        </MotionText>

        <div className="flex flex-col">
          <TimelineStep
            stepNumber={1}
            date={t('taxes.vat.step1Date')}
            title={t('taxes.vat.step1Title')}
            delay={0.1}
          >
            <StepItem icon={<PiDownload className="w-4 h-4" />}>
              {t('taxes.vat.step1Download')}
            </StepItem>
            <div className="space-y-2">
              <StepLink href={VAT_LINKS.boltInvoices} icon={<PiLink className="w-4 h-4" />}>
                {t('taxes.vat.boltInvoices')}
              </StepLink>
            </div>
            <StepItem icon={<PiUpload className="w-4 h-4" />}>
              {t('taxes.vat.step1Upload')}
            </StepItem>
          </TimelineStep>

          <TimelineStep
            stepNumber={2}
            date={t('taxes.vat.step2Date')}
            title={t('taxes.vat.step2Title')}
            delay={0.2}
          >
            <StepItem icon={<PiArrowRight className="w-4 h-4" />}>
              <Trans i18nKey="taxes.vat.step2Eds" components={{ strong: <strong /> }} />
            </StepItem>
            <StepItem icon={<PiInfo className="w-4 h-4" />}>
              {t('taxes.vat.step2Amount')}
            </StepItem>
            <div className="space-y-2">
              <StepLink href={VAT_LINKS.videoGuide} icon={<PiYoutubeLogo className="w-4 h-4" />}>
                {t('taxes.vat.videoGuide')}
              </StepLink>
              <StepLink href={VAT_LINKS.edsForm} icon={<PiLink className="w-4 h-4" />}>
                {t('taxes.vat.edsFormList')}
              </StepLink>
            </div>
          </TimelineStep>

          <TimelineStep
            stepNumber={3}
            date={t('taxes.vat.step3Date')}
            title={t('taxes.vat.step3Title')}
            delay={0.3}
          >
            <div className="bg-zinc-50 rounded-lg p-5 text-sm space-y-3">
              <p className="font-medium text-zinc-900">{t('taxes.vat.paymentDetails')}</p>
              <ul className="space-y-1 text-zinc-700">
                <li><strong>{t('taxes.vat.recipient')}</strong> {t('taxes.vat.recipientValue')}</li>
                <li><strong>{t('taxes.vat.paymentCode')}</strong> 1001</li>
                <li>{t('taxes.vat.accountNumber')}</li>
                <li>{t('taxes.vat.payerDetails')}</li>
                <li>{t('taxes.vat.amount')}</li>
              </ul>
            </div>
            <div className="space-y-2">
              <StepLink href={VAT_LINKS.vmiAccounts} icon={<PiLink className="w-4 h-4" />}>
                {t('taxes.vat.collectionAccounts')}
              </StepLink>
              <StepLink href={VAT_LINKS.vmiPayment} icon={<PiLink className="w-4 h-4" />}>
                {t('taxes.vat.vmiPaymentGuide')}
              </StepLink>
            </div>
          </TimelineStep>

          <TimelineStep
            stepNumber={4}
            date={t('taxes.vat.step4Date')}
            title={t('taxes.vat.step4Title')}
            isLast
            delay={0.4}
          >
            <StepItem icon={<PiChartBar className="w-4 h-4" />}>
              {t('taxes.vat.step4Auto')}
            </StepItem>
            <StepItem icon={<PiArrowRight className="w-4 h-4" />}>
              <Trans i18nKey="taxes.vat.step4Full" components={{ strong: <strong /> }} />
            </StepItem>
          </TimelineStep>
        </div>
        <motion.div
          className="absolute bottom-0 -ml-5 px-5 pb-8 pt-8 w-full bg-linear-to-t from-white via-white to-transparent from-0% via-40% to-100%"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2, ease: "easeOut" }}
        >
          <ButtonLink to={'/dashboard'}>
            {t('taxes.vat.understood')}
          </ButtonLink>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardVatMemoPage;